/**
 * My Bids list — profile tab showing buyer offers with product context.
 * Layout: image left, details right; card links to product; actions for message / cart.
 */
import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  MessageCircle,
  ShoppingCart,
  ExternalLink,
  Loader2,
  ChevronRight,
} from 'lucide-react';
import { LoadingSpinner, EmptyState } from '../UI';
import {
  formatPrice,
  formatDateTime,
  getProductImageUrl,
  getProductName,
  getProductId,
} from '../../utils/helpers';
import {
  getBidStatusColor,
  getBidStatusText,
  matchesBidFilter,
  isAcceptedBidAddable,
  normalizeBidStatus,
} from '../../utils/bidDisplay';

const PLACEHOLDER = '/placeholder.jpg';

const BidCard = ({
  bid,
  t,
  onAddToCart,
  isAddingBidToCart,
  onMarkNotificationsRead,
}) => {
  const navigate = useNavigate();
  const [imgSrc, setImgSrc] = useState(() =>
    getProductImageUrl(bid.product || { _id: bid.productId })
  );

  const bidId = bid._id || bid.id;
  const productId = getProductId(bid.product) || bid.productId;
  const productName = getProductName(bid.product);
  const statusText = getBidStatusText(bid.status, t);
  const statusColor = getBidStatusColor(bid.status);
  const normalizedStatus = normalizeBidStatus(bid.status);
  const isUnread = false;
  const canAddToCart = isAcceptedBidAddable(bid);
  const isWinning = normalizedStatus === 'accepted' || normalizedStatus === 'converted';

  const openProduct = () => {
    if (!productId) return;
    onMarkNotificationsRead?.(bid);
    navigate(`/product/${productId}`);
  };

  const openConversation = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onMarkNotificationsRead?.(bid);
    navigate('/conversations', {
      state: {
        conversationId: bid.conversation || undefined,
        productId: productId || undefined,
        openOffer: normalizedStatus === 'pending',
      },
    });
  };

  const handleAddToCart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onAddToCart?.(bid);
  };

  return (
    <article
      className={`bid-card${isUnread ? ' bid-card--unread' : ''}${isWinning ? ' bid-card--winning' : ''}`}
    >
      <button
        type="button"
        className="bid-card__main"
        onClick={openProduct}
        aria-label={t('offers.viewProduct', { name: productName })}
      >
        <div className="bid-card__media">
          <img
            src={imgSrc}
            alt={productName}
            className="bid-card__image"
            loading="lazy"
            onError={() => setImgSrc(PLACEHOLDER)}
          />
          {isUnread && <span className="bid-card__unread-dot" aria-hidden />}
        </div>

        <div className="bid-card__body">
          <div className="bid-card__top">
            <h4 className="bid-card__title">{productName}</h4>
            <span
              className="bid-card__status"
              style={{ backgroundColor: statusColor }}
            >
              {statusText}
            </span>
          </div>

          {bid.seller?.name && (
            <p className="bid-card__seller">{bid.seller.name}</p>
          )}

          <div className="bid-card__meta">
            <span className="bid-card__price">
              {formatPrice(bid.bidPrice || bid.amount || 0)}
            </span>
            <time className="bid-card__date" dateTime={bid.createdAt || undefined}>
              {bid.createdAt
                ? formatDateTime(bid.createdAt)
                : t('offers.dateUnknown')}
            </time>
          </div>

          {bid.expiresAt && normalizedStatus === 'accepted' && (
            <p className="bid-card__expires">
              {t('offers.expires')} {formatDateTime(bid.expiresAt)}
            </p>
          )}
        </div>

        <ChevronRight size={18} className="bid-card__chevron" aria-hidden />
      </button>

      <div className="bid-card__actions">
        <button
          type="button"
          className="bid-card__action bid-card__action--secondary"
          onClick={openConversation}
        >
          <MessageCircle size={16} />
          {t('offers.messageSeller')}
        </button>

        {canAddToCart && (
          <button
            type="button"
            className="bid-card__action bid-card__action--primary"
            disabled={isAddingBidToCart}
            onClick={handleAddToCart}
          >
            <ShoppingCart size={16} />
            {t('offers.addToCart')}
          </button>
        )}

        {productId && (
          <button
            type="button"
            className="bid-card__action bid-card__action--ghost"
            onClick={(e) => {
              e.stopPropagation();
              openProduct();
            }}
          >
            <ExternalLink size={16} />
            {t('offers.viewProductShort')}
          </button>
        )}
      </div>
    </article>
  );
};

const MyBidsList = ({
  bids = [],
  isLoading,
  hasMore,
  isLoadingMore,
  onLoadMore,
  onAddToCart,
  isAddingBidToCart,
  onMarkNotificationsRead,
}) => {
  const { t } = useTranslation('profile');
  const [statusFilter, setStatusFilter] = useState('all');

  const filteredBids = useMemo(
    () => bids.filter((bid) => matchesBidFilter(bid, statusFilter)),
    [bids, statusFilter]
  );

  const filters = [
    { id: 'all', label: t('offers.filterAll') },
    { id: 'pending', label: t('offers.filterPending') },
    { id: 'accepted', label: t('offers.filterAccepted') },
    { id: 'closed', label: t('offers.filterClosed') },
  ];

  return (
    <div className="bids-section">
      <div className="bids-section__header">
        <p className="bids-section__subtitle">{t('offers.subtitle')}</p>
        <Link to="/conversations" className="bids-section__messages-link">
          <MessageCircle size={16} />
          {t('offers.viewAllMessages')}
        </Link>
      </div>

      <div className="bids-filters" role="tablist" aria-label={t('offers.filterLabel')}>
        {filters.map(({ id, label }) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={statusFilter === id}
            className={`bids-filter-btn${statusFilter === id ? ' active' : ''}`}
            onClick={() => setStatusFilter(id)}
          >
            {label}
          </button>
        ))}
      </div>

      {isLoading ? (
        <LoadingSpinner text={t('offers.loading')} />
      ) : bids.length === 0 ? (
        <EmptyState
          icon="messages"
          title={t('offers.noneYet')}
          description={t('offers.browseHint')}
          actionText={t('offers.browseAction')}
          actionLink="/"
        />
      ) : filteredBids.length === 0 ? (
        <EmptyState
          icon="messages"
          title={t('offers.noFilterResults')}
          description={t('offers.tryAnotherFilter')}
          actionText={t('offers.filterAll')}
          onAction={() => setStatusFilter('all')}
        />
      ) : (
        <>
          <div className="bids-list">
            {filteredBids.map((bid, idx) => {
              const bidId = bid._id || bid.id;
              const key = bidId
                ? `${bidId}-${idx}`
                : `bid-${idx}-${bid.createdAt || ''}`;
              return (
                <BidCard
                  key={key}
                  bid={bid}
                  t={t}
                  onAddToCart={onAddToCart}
                  isAddingBidToCart={isAddingBidToCart}
                  onMarkNotificationsRead={onMarkNotificationsRead}
                />
              );
            })}
          </div>

          {hasMore && (
            <div className="bids-load-more">
              <button
                type="button"
                className="bids-load-more-btn"
                onClick={onLoadMore}
                disabled={isLoadingMore}
              >
                {isLoadingMore ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    {t('offers.loadingMore', { defaultValue: 'Loading...' })}
                  </>
                ) : (
                  t('offers.loadMore', { defaultValue: 'Load more' })
                )}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default MyBidsList;
